import { useMemo } from "react";
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useNormalizedCompany } from "../../Helpers/useNormalizeCompany";
import { Grid } from "@mantine/core";
import { Company, PartialCompanyRegistration, PartialCreateAddress, Address } from "@/types";
import { useTranslation } from "react-i18next";
import Text from "@/components/atoms/Text";
import Accordion from "@/components/organisms/AccordionDrawer";
import Table from "@/components/atoms/Table";
import React from "react";

interface SharedCompanyInfoTabProps {
  company: PartialCompanyRegistration | Company;
}

export default function SharedCompanyInfoTab({ company }: SharedCompanyInfoTabProps) {
  const { t } = useTranslation();

  const normalizedCompany = useNormalizedCompany(company);

  return (
    <>
      <Grid p="lg">
        <GridRow label={t("common:companyName")} value={normalizedCompany.name} />
        <GridRow label={t("common:email")} value={normalizedCompany.email} />
        <GridRow label={t("common:phone")} value={normalizedCompany.phone} />
        <GridRow label={t("common:language")} value={normalizedCompany.language} />
        <GridRow label={t("common:timeZone")} value={normalizedCompany.timeZone} />
        <GridRow label={t("common:currency")} value={normalizedCompany.defaultCurrency} />
        <GridRow label={t("common:companyType")} value={normalizedCompany.companyType} />
        <GridRow label={t("common:membershipType")} value={normalizedCompany.membershipType} />
        <GridRow label={t("common:vatNumber")} value={normalizedCompany.vatNumber} />
        <GridRow label={t("common:leiNumber")} value={normalizedCompany.leiNumber} />
        <GridRow label={t("common:eoriNumber")} value={normalizedCompany.eoriNumber} />
      </Grid>
      <Accordion title={t("common:address")}>
        <AddressAccordionContent address={normalizedCompany.address} />
      </Accordion>
    </>
  );
}

const GridRow = ({ label, value }: { label: string; value: string | null }) => {
  return (
    <>
      <Grid.Col span={4}>
        <Text color="secondary" size="sm">
          {label}
        </Text>
      </Grid.Col>
      <Grid.Col span={8}>
        <Text size="sm">{value ?? "-"}</Text>
      </Grid.Col>
    </>
  );
};

const AddressAccordionContent = ({ address }: { address: PartialCreateAddress[] | Address[] }) => {
  const { t } = useTranslation();

  const columns: ColumnDef<PartialCreateAddress | Address>[] = useMemo(
    () => [
      {
        header: () => <Table.Th>{t("common:address")}</Table.Th>,
        accessorKey: "address",
      },
      {
        header: () => <Table.Th>{t("common:postCode")}</Table.Th>,
        accessorKey: "postCode",
      },
      {
        header: () => <Table.Th>{t("common:place")}</Table.Th>,
        accessorKey: "place",
      },
      {
        header: () => <Table.Th>{t("common:country")}</Table.Th>,
        accessorKey: "country",
        cell: ({ row }) => ("country" in row.original ? row.original.country.name : "-"),
      },
      {
        header: () => <Table.Th>{t("common:addressType")}</Table.Th>,
        accessorKey: "addressType",
      },
    ],
    []
  );

  const table = useReactTable({
    data: address,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Table>
      <Table.Thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <Table.Tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <React.Fragment key={header.id}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </React.Fragment>
            ))}
          </Table.Tr>
        ))}
      </Table.Thead>
      <Table.Tbody>
        {table.getRowModel().rows.map((row) => (
          <Table.Tr key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <Table.Td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Table.Td>
            ))}
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
};
